import { Form } from "@circle-react-uikit/Form";
import {
  useReportPostMutation,
  type ReportPostFormValues,
} from "@circle-react/components/PostsV3/hooks/useReportPostMutation";
import type { Post } from "@circle-react/types";
import { Modal } from "@circleco/compass/components/Modal";

import { t } from "@/i18n-js/instance";

const FORM_ID = "report-post-modal-form";

const REPORT_REASONS = [
  "harassment",
  "spam",
  "incorrect_location",
  "against_guidelines",
  "other",
] as const;

export interface ReportPostModalComponentProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  post: Post;
}

export const ReportPostModalComponent = ({
  onOpenChange,
  open,
  post,
}: ReportPostModalComponentProps) => {
  const { mutate: submitReport, isPending: isReporting } =
    useReportPostMutation({
      postId: post.id,
      onSuccess: () => onOpenChange(false),
    });

  const reportReasonOptions = REPORT_REASONS.map(reason => ({
    value: reason,
    label: t(`flagged_contents.report_reasons.${reason}`),
  }));

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange} size="md">
      <Modal.Content>
        <Modal.Header title={t("flagged_contents.form.report_post")} />
        <Modal.Body>
          <Form<ReportPostFormValues>
            id={FORM_ID}
            defaultValues={{
              reported_reason_body: "",
              reported_reason_type: "",
            }}
            onSubmit={data => submitReport(data)}
          >
            <Form.Item
              description={t(
                "flagged_contents.form.reported_reason_type_description",
              )}
              hideBorder
              isFirstChild
              label={t("flagged_contents.form.reported_reason_type")}
              name="reported_reason_type"
              rules={{
                required: t("settings.validation.this_field_is_required"),
              }}
            >
              <Form.SelectNative
                name="reported_reason_type"
                options={reportReasonOptions}
                prompt={t("flagged_contents.form.reported_reason_type_select")}
                data-testid="report-reason-type-select"
              />
            </Form.Item>
            <Form.Item
              description={t(
                "flagged_contents.form.reported_reason_body_description",
              )}
              fullWidth
              hideBorder
              label={t("flagged_contents.form.reported_reason_body")}
              name="reported_reason_body"
              placeholder={t(
                "flagged_contents.form.reported_reason_body_placeholder",
              )}
            >
              <Form.TextArea name="reported_reason_body" />
            </Form.Item>
          </Form>
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t("cancel"),
            onClick: () => onOpenChange(false),
          }}
          primaryAction={{
            label: t("report"),
            type: "submit",
            form: FORM_ID,
            loading: isReporting,
            disabled: isReporting,
            "data-testid": "report_post_submit",
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
